import { useState, useEffect, useCallback } from 'react';
import { Sparkles, Lock, Loader2, ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import AlbumCover from '@/components/albums/AlbumCover';

const API = API_BASE;

/**
 * Smart Collections — AI-grouped themed photo collections (Premium feature).
 * Renders a horizontally-scrollable strip of theme cards above the user's
 * regular Albums grid. Hidden for users with 0 themed photos to avoid noise.
 */
export default function SmartCollections() {
  const [items, setItems] = useState([]);
  const [isPremium, setIsPremium] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/smart-collections`, { withCredentials: true });
      setItems(data.items || []);
      setIsPremium(!data.is_premium_feature ? true : (data.items || []).length > 0);
    } catch {
      /* silent — feature is best-effort */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return null;
  // Hide the strip entirely if there are no themed photos yet — premium upsell
  // for this feature is already in the OnboardingWizard + ProactivePaywall.
  if (items.length === 0) return null;

  return (
    <section className="mb-8" data-testid="smart-collections">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">
          <Sparkles className="w-4 h-4" />
        </div>
        <h2 className="text-lg font-semibold text-stone-900">Smart collections</h2>
        <span className="text-xs text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full font-medium">
          AI · Beta
        </span>
        {!isPremium && <Lock className="w-3.5 h-3.5 text-stone-400 ml-1" />}
      </div>
      <p className="text-sm text-stone-500 mb-4">
        Auto-grouped from your photos&apos; people, places and moments.
      </p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
        {items.map((c) => (
          <Link
            key={c.theme}
            to={`/collections/${encodeURIComponent(c.theme)}`}
            className="snap-start shrink-0 w-40 group"
            data-testid={`collection-${c.theme}`}
          >
            <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100 relative">
              {c.cover_file_id ? (
                <AlbumCover fileId={c.cover_file_id} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-stone-300" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0" />
              <div className="absolute bottom-2 left-2 right-2 text-white">
                <p className="text-sm font-semibold truncate drop-shadow">{c.theme}</p>
                <p className="text-xs opacity-90">{c.photo_count} photo{c.photo_count === 1 ? '' : 's'}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function SmartCollectionsLoading() {
  return (
    <div className="mb-8 flex items-center gap-2 text-sm text-stone-400">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading smart collections…
    </div>
  );
}
